import { path, useWordsearchCells } from "./useWordsearchCells"
import "../crossword/Crossword.scss"
import { config } from "../../../config"
import { say } from "../../../helpers/speech.js"
import { useTranslation } from "react-i18next"
import { useContext } from "react"
import { UserContext } from "../../../store/store"

const WordListHints = ({ clues, progress }) => (
  <center>
    {clues.map((clue, ix) => (
      <p
        key={ix}
        className={
          "hint" +
          (progress.dones.includes(clue.definicion.toUpperCase())
            ? " done"
            : "")
        }>
        <span className="boldy">{ix + 1}</span>
        <span
          className="texty"
          onClick={() => say(clue.definicion)}
          style={{ cursor: "pointer" }}>
          {progress.dones.includes(clue.definicion.toUpperCase()) ? (
            <del>{clue.definicion.toUpperCase()}</del>
          ) : (
            clue.definicion.toUpperCase()
          )}
        </span>
      </p>
    ))}
  </center>
)

const CluesHints = ({ clues, progress }) =>
  clues.map((clue, ix) => (
    <p
      key={ix}
      className={
        "hint" +
        (progress.dones.includes(clue.definicion.toUpperCase()) ? " done" : "")
      }>
      <span className="boldy">{ix + 1}</span>
      <span
        onClick={() => say(clue.definiendum)}
        className="texty"
        style={{ cursor: "pointer" }}>
        {progress.dones.includes(clue.definicion.toUpperCase()) ? (
          <del>{clue.definiendum}</del>
        ) : (
          clue.definiendum
        )}
      </span>
    </p>
  ))

const OneClueHints = ({ clues, tag, progress }) => (
  <center>
    <p>
      <span>
        Find &nbsp;
        <span className="boldy">{clues.length - progress.total}</span> words in
        the category &nbsp;<span className="boldy">{tag ? tag : "?"}</span>
      </span>
    </p>
  </center>
)

const ImageHints = ({ clues, tag, progress }) => {
  const { t } = useTranslation()
  const [state] = useContext(UserContext)

  return (
    <>
      <center>
        <div className="row">
          {clues.map((clue, ix) => {
            const url = clue.file
                ? `${config.imgUrl}/words/${tag.toLowerCase()}/${clue.file}`
                : `${config.imgUrl}/404.png`,
              done = progress.dones.includes(clue.definicion.toUpperCase())

            return (
              <div
                key={ix}
                onClick={() =>
                  say(
                    clue.definicion.toUpperCase(),
                    undefined,
                    undefined,
                    undefined,
                    true
                  )
                }
                style={{ cursor: "pointer" }}
                className="col-3 selectable">
                <img
                  title={clue.definicion}
                  alt={clue.file}
                  src={url}
                  className={"w-100 pb-2" + (done ? " img-done" : "")}
                />
              </div>
            )
          })}
        </div>
        <div className="d-flex flex-row justify-content-center mt-4">
          <div className={"sound-alert " + state.theme}>
            {t("games.wordsearch.click_images")}
          </div>
        </div>
      </center>
    </>
  )
}
const TextHint = ({ clues, text, progress }) => (
  <center>
    <p>{text}</p>
  </center>
)

const Hints = ({ clues, tag, difficulty, progress, text }) => {
  const { t } = useTranslation()

  if (!clues) return <span></span>

  const isWordlist = difficulty === "wordlist",
    isClues = difficulty === "clues",
    isoneclue = difficulty === "oneclue",
    isImg = difficulty === "imgs",
    isText = difficulty === "text"

  const title = t(`games.wordsearch.difficulties.${difficulty}.hints_title`)

  return (
    <>
      <center>
        <h5 className="shady">{title}</h5>
      </center>
      <div className="row mx-3 pt-3">
        {isWordlist && <WordListHints clues={clues} progress={progress} />}
        {isClues && <CluesHints clues={clues} progress={progress} />}
        {isoneclue && (
          <OneClueHints clues={clues} tag={tag} progress={progress} />
        )}
        {isImg && <ImageHints clues={clues} tag={tag} progress={progress} />}
        {isText && (
          <TextHint clues={clues} text={text} tag={tag} progress={progress} />
        )}
      </div>
    </>
  )
}

const CellEl = ({ cell, selected }) => {
  let cssClasses = "cell"

  if (cell.done)
    cssClasses +=
      ` done-color-${cell.color} done-color-${cell.word}` +
      (cell.overloaded ? " overloaded" : "")

  if (selected.some((item) => item.x === cell.x && item.y === cell.y))
    cssClasses += " selected"

  return (
    <div
      className={cssClasses}
      data-y={cell.y}
      data-x={cell.x}
      draggable="false">
      {cell.char}
    </div>
  )
}

const Grid = ({ cells, tracking, selection }) => {
  let selected = tracking ? path(selection) : []
  const [state] = useContext(UserContext)

  return (
    <div id="category-container" className="container-sm container-mini">
      <div className={"crosswords unselectable " + state.theme}>
        {cells.map((row, rix) => (
          <div key={rix} className="cell-row">
            {row.map((cell, cix) => (
              <CellEl key={cix} selected={selected} cell={cell} />
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

const Progress = ({ ratio }) => {
  const p = Math.round(ratio * 100) + "%"
  return (
    <div className="mb-4 container-sm container-mini progress-holder">
      <div className="progress-bar" style={{ width: p }}>
        <span>{p}</span>
      </div>
    </div>
  )
}

const Wordsearch = ({ difficulty, data, winCallback }) => {
  const cells = useWordsearchCells(data, winCallback)
  const { t } = useTranslation()

  const [state] = useContext(UserContext)

  return (
    <div className="row my-4">
      <div className="col-lg-2 col-12"></div>
      <div className="col-lg-4 col-12 py-4">
        <Hints
          clues={data.clues}
          text={data.text}
          difficulty={difficulty}
          progress={cells.progress}
          tag={data.tag}
        />
      </div>

      <div className="col-lg-4 col-12">
        <div className="d-flex flex-row justify-content-center mb-4">
          <div className={"sound-alert " + state.theme}>
            🔊 {t(`ui.games.msgs.volume_up`)}
          </div>
        </div>
        <Progress ratio={cells.progress.ratio} />
        <Grid
          cells={cells.matrix}
          tracking={cells.tracking}
          selection={cells.selection}
        />
      </div>

      <div className="col-lg-2 col-12"></div>
    </div>
  )
}

export default Wordsearch
